import { useEffect, useState } from 'react';
import { useToastApi } from '../utils/toastApi';

const commands = [
  { 
    id: 'new-tale', 
    label: 'Create New Tale', 
    description: 'Generate a new bilingual tale with AI',
    href: '/tales/new',
    icon: '📖'
  },
  { 
    id: 'dashboard', 
    label: 'Go to Dashboard', 
    description: 'View overview and recent activity',
    href: '/dashboard',
    icon: '🏠'
  },
  { 
    id: 'my-videos', 
    label: 'My Videos', 
    description: 'View uploaded YouTube videos',
    href: '/my-videos',
    icon: '🎬'
  },
  { 
    id: 'tales', 
    label: 'All Tales', 
    description: 'Browse and manage all tales',
    href: '/tales',
    icon: '📚'
  },
  { 
    id: 'images', 
    label: 'Images', 
    description: 'View generated scene images',
    href: '/images',
    icon: '🎨'
  },
  { 
    id: 'settings', 
    label: 'Settings', 
    description: 'Account and preferences',
    href: '/settings',
    icon: '⚙️'
  },
  { 
    id: 'bulk-images', 
    label: 'Generate Images (Bulk)', 
    description: 'Generate images for selected tales',
    emit: 'bulk:images',
    icon: '🖼️'
  },
  { 
    id: 'bulk-render', 
    label: 'Render Videos (Bulk)', 
    description: 'Package videos for selected tales',
    emit: 'bulk:render',
    icon: '🎞️'
  },
  { 
    id: 'bulk-upload', 
    label: 'Upload to YouTube (Bulk)', 
    description: 'Upload selected tales to YouTube',
    emit: 'bulk:upload',
    icon: '📺'
  }
];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const toastApi = useToastApi();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(v => !v);
        setQuery('');
      }
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!open) return null;

  const filtered = commands.filter(c => 
    c.label.toLowerCase().includes(query.toLowerCase()) ||
    c.description.toLowerCase().includes(query.toLowerCase())
  );

  const run = (cmd: any) => {
    setOpen(false);
    setQuery('');
    
    if (cmd.href) {
      window.location.href = cmd.href;
    }
    
    if (cmd.emit) {
      const selectedCount = document.querySelectorAll('input[type="checkbox"]:checked').length;
      if (selectedCount === 0) {
        toastApi.info('No tales selected', 'Select some tales first to use bulk actions');
        return;
      }
      window.dispatchEvent(new CustomEvent(cmd.emit));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && filtered.length > 0) {
      run(filtered[0]);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center pt-24">
      <div className="w-full max-w-2xl mx-4">
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden">
          {/* Search Input */}
          <div className="p-4 border-b border-gray-200 dark:border-slate-700">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                autoFocus
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a command or search..."
                className="w-full pl-10 pr-4 py-3 text-lg bg-transparent border-none focus:outline-none text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
              />
              <kbd className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                ESC
              </kbd>
            </div>
          </div>

          {/* Commands List */}
          <div className="max-h-96 overflow-auto">
            {filtered.length > 0 ? (
              <div className="py-2">
                {filtered.map((cmd, index) => (
                  <button
                    key={cmd.id}
                    onClick={() => run(cmd)}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-slate-800 flex items-center gap-3 transition-colors ${
                      index === 0 ? 'bg-gray-50 dark:bg-slate-800' : ''
                    }`}
                  >
                    <span className="text-xl">{cmd.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {cmd.label}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {cmd.description}
                      </div>
                    </div>
                    {cmd.emit && (
                      <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300 px-2 py-1 rounded">
                        BULK
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p className="text-gray-500 dark:text-gray-400">No commands found</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Try searching for "create", "generate", or "upload"
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 bg-gray-50 dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700">
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <kbd className="bg-white dark:bg-gray-700 px-1.5 py-0.5 rounded">⏎</kbd>
                  to select
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="bg-white dark:bg-gray-700 px-1.5 py-0.5 rounded">↑↓</kbd>
                  to navigate
                </span>
              </div>
              <span className="flex items-center gap-1">
                <kbd className="bg-white dark:bg-gray-700 px-1.5 py-0.5 rounded">⌘K</kbd>
                to toggle
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
